import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useAuth } from '../Contexts/AuthContext';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  AiKeyApi,
  AiKeyApiContextValue,
  AiKeyDetailApi,
  AiKeyListResponse,
  AiKeyOptionsResponse,
  AiKeyPayload,
  DeleteAiKeyResponse,
} from './AiKeyApiProviderTypes';

const AiKeyApiContext = createContext<AiKeyApiContextValue | undefined>(undefined);

const BASE_PATH = '/v1/ai_keys';

export const AiKeyApiProvider = ({ children }: { children: ReactNode }) => {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ORGANIZATION);
  const auth = useAuth();

  const getAiKeys = useCallback(
    async (skip?: number, limit?: number): Promise<AiKeyListResponse> => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }

      try {
        const response = await get(`${BASE_PATH}?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching AI keys:', error);
        throw error;
      }
    },
    [get, auth],
  );

  const getAiKeyOptions = useCallback(async (): Promise<AiKeyOptionsResponse> => {
    try {
      const response = await get(`${BASE_PATH}/types`);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI key options:', error);
      throw error;
    }
  }, [get]);

  const getAiKeyById = useCallback(
    async (keyId: string): Promise<AiKeyDetailApi> => {
      try {
        const response = await get(`${BASE_PATH}/${keyId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching AI key by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createAiKey = useCallback(
    async (body: AiKeyPayload): Promise<AiKeyApi> => {
      try {
        const response = await post(BASE_PATH, body);
        return response.data;
      } catch (error) {
        console.error('Error creating AI key:', error);
        throw error;
      }
    },
    [post],
  );

  const updateAiKey = useCallback(
    async (keyId: string, body: AiKeyPayload): Promise<AiKeyApi> => {
      try {
        const response = await put(`${BASE_PATH}/${keyId}`, body);
        return response.data;
      } catch (error) {
        console.error('Error updating AI key:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteAiKey = useCallback(
    async (keyId: string): Promise<DeleteAiKeyResponse> => {
      try {
        const response = await del(`${BASE_PATH}/${keyId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting AI key:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <AiKeyApiContext.Provider
      value={{
        getAiKeys,
        getAiKeyOptions,
        getAiKeyById,
        createAiKey,
        updateAiKey,
        deleteAiKey,
      }}
    >
      {children}
    </AiKeyApiContext.Provider>
  );
};

export const useAiKeyApi = (): AiKeyApiContextValue => {
  const context = useContext(AiKeyApiContext);
  if (!context) {
    throw new Error('useAiKeyApi must be used within an AiKeyApiProvider');
  }
  return context;
};
